import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import { useAuth } from '#globalContexts/AuthProvider';
import { CSV_COLUMN_GROUPS } from '#leadOverviewFeature/constants/csvExportColumns';
import { useTargetingVocabulary } from '#leadOverviewFeature/models/targeting/queries';

/**
 * Column selector component for CSV export
 * Groups columns into logical categories with select all/none functionality
 */
const ColumnSelector = ({ selectedColumns, onChange }) => {
	const { capabilities } = useAuth();
	const { data: targetingVocabulary } = useTargetingVocabulary();
	// Columns an add-on fills (vendor phone numbers) are not offered to orgs without it.
	const columnGroups = Object.fromEntries(
		Object.entries(CSV_COLUMN_GROUPS)
			.filter(([, group]) => !group.requiresTargetingSearch || targetingVocabulary?.searchEnabled)
			.map(([key, group]) => [
				key,
				{
					...group,
					columns: group.columns.filter(
						(col) => !col.requiresCapability || capabilities.includes(col.requiresCapability),
					),
				},
			]),
	);
	const [expandedGroups, setExpandedGroups] = useState({
		company: true,
		ai: true,
		contact: true,
		financial: true,
		websiteProfile: true,
	});

	// Toggle group expansion
	const toggleGroup = (groupKey) => {
		setExpandedGroups((prev) => ({
			...prev,
			[groupKey]: !prev[groupKey],
		}));
	};

	// Check if column is selected
	const isColumnSelected = (columnKey) => {
		return selectedColumns.includes(columnKey);
	};

	// Toggle single column
	const toggleColumn = (columnKey) => {
		if (selectedColumns.includes(columnKey)) {
			onChange(selectedColumns.filter((key) => key !== columnKey));
		} else {
			onChange([...selectedColumns, columnKey]);
		}
	};

	// Select all columns in a group
	const selectAllInGroup = (groupKey) => {
		const groupColumns = columnGroups[groupKey].columns.map((col) => col.key);
		const otherColumns = selectedColumns.filter((key) => !groupColumns.includes(key));
		onChange([...otherColumns, ...groupColumns]);
	};

	// Deselect all columns in a group
	const deselectAllInGroup = (groupKey) => {
		const groupColumns = columnGroups[groupKey].columns.map((col) => col.key);
		onChange(selectedColumns.filter((key) => !groupColumns.includes(key)));
	};

	// Count selected columns in a group
	const getSelectedCount = (groupKey) => {
		const groupColumns = columnGroups[groupKey].columns.map((col) => col.key);
		return groupColumns.filter((key) => selectedColumns.includes(key)).length;
	};

	// Check if all columns in group are selected
	const isGroupFullySelected = (groupKey) => {
		const groupColumns = columnGroups[groupKey].columns.map((col) => col.key);
		return groupColumns.every((key) => selectedColumns.includes(key));
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between mb-2">
				<h3 className="text-md font-medium text-text">Spalten auswählen</h3>
				<span className="text-sm text-bgBase-800">
					{selectedColumns.length} von{' '}
					{Object.values(columnGroups).reduce((acc, group) => acc + group.columns.length, 0)} ausgewählt
				</span>
			</div>

			{Object.entries(columnGroups).map(([groupKey, group]) => {
				const isExpanded = expandedGroups[groupKey];
				const selectedCount = getSelectedCount(groupKey);
				const totalCount = group.columns.length;
				const isFullySelected = isGroupFullySelected(groupKey);

				return (
					<div key={groupKey} className="border border-gray-200 rounded-lg overflow-hidden">
						{/* Group header */}
						<div className="bg-gray-50 p-3">
							<div className="flex items-center justify-between">
								<button
									type="button"
									onClick={() => toggleGroup(groupKey)}
									className="flex items-center gap-2 text-sm font-medium text-text hover:text-text transition-colors"
								>
									{isExpanded ? (
										<BsChevronDown className="text-bgBase-800" />
									) : (
										<BsChevronRight className="text-bgBase-800" />
									)}
									{group.label}
									<span
										className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
											selectedCount === totalCount
												? 'bg-purple-100 text-primary-700'
												: selectedCount > 0
													? 'bg-primary-200 text-primary-700'
													: 'bg-gray-100 text-text'
										}`}
									>
										{selectedCount}/{totalCount}
									</span>
								</button>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => selectAllInGroup(groupKey)}
										disabled={isFullySelected}
										className="text-xs"
									>
										Alle
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={() => deselectAllInGroup(groupKey)}
										disabled={selectedCount === 0}
										className="text-xs"
									>
										Keine
									</Button>
								</div>
							</div>
						</div>

						{/* Group columns */}
						{isExpanded && (
							<div className="p-3 bg-white">
								<div className="grid grid-cols-2 gap-2">
									{group.columns.map((column) => (
										<Checkbox
											key={column.key}
											id={`csv-export-column-${column.key}`}
											label={column.label}
											checked={isColumnSelected(column.key)}
											onChange={() => toggleColumn(column.key)}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				);
			})}

			{/* Warning if no columns selected */}
			{selectedColumns.length === 0 && (
				<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
					<p className="text-sm text-caution-700">Bitte wählen Sie mindestens eine Spalte aus.</p>
				</div>
			)}
		</div>
	);
};

ColumnSelector.propTypes = {
	selectedColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
	onChange: PropTypes.func.isRequired,
};

export default ColumnSelector;
